// CHG-1416 / ITER-3536 — v2 NotFound page (DRIFT-649 close).
//
// Mounted as the catch-all (`<Route path="*">`) inside the AppShell-wrapped
// nested <Routes> block in App.tsx. Before this CHG, any URL that didn't
// match a declared inner route rendered an empty AppShell chrome — no copy,
// no link back, no way for the user to recover except editing the URL bar.
//
// Design notes:
//   - Renders INSIDE AppShell, so no header/wordmark/login-bg chrome here;
//     the shell already supplies that. This file is just the content slot.
//   - The pathname surfaces so users can see what they typed (debuggability)
//     and ops can read it off a screenshot.
//   - Two CTAs — a primary "Back to Overview" Link and a secondary "Go back"
//     button that uses window.history.back() for the bookmarked-stale case
//     where the prior page IS in history.
//   - Inline styles + CSS vars per the AccessDenied / WelcomeOverlay /
//     SetupProgress sibling pattern in this app — no Tailwind, no module
//     CSS. Brand colors come from --color-primary / --color-text / etc.
//   - data-page="not-found" attribute is the test hook; the vitest in
//     __tests__/NotFound.test.tsx asserts it renders for unknown paths.

import { Link, useLocation } from "react-router-dom";

export function NotFound() {
  const { pathname } = useLocation();
  return (
    <main
      data-page="not-found"
      data-pathname={pathname}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "60vh",
        padding: 24,
      }}
    >
      <div
        data-card="not-found"
        style={{
          background: "var(--color-surface)",
          borderRadius: "var(--radius-lg, 12px)",
          boxShadow: "var(--shadow-medium)",
          padding: "40px 32px",
          width: "100%",
          maxWidth: 480,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            fontSize: 56,
            fontWeight: 600,
            lineHeight: 1,
            color: "var(--color-text-muted)",
            fontFamily: "var(--font-display, inherit)",
            letterSpacing: "-0.02em",
          }}
        >
          404
        </div>
        <h1
          style={{
            fontSize: 20,
            fontWeight: 600,
            margin: 0,
            color: "var(--color-text)",
          }}
        >
          Page not found
        </h1>
        <p
          style={{
            margin: 0,
            fontSize: 14,
            color: "var(--color-text-muted)",
            lineHeight: 1.5,
          }}
        >
          We couldn't find anything at this URL. It may have been moved, or the link
          you followed may be out of date.
        </p>
        <div
          data-role="pathname"
          style={{
            padding: "6px 10px",
            fontSize: 12,
            fontFamily: "var(--font-mono, monospace)",
            background: "var(--color-bg-subtle)",
            color: "var(--color-text-muted)",
            borderRadius: "var(--radius-sm, 6px)",
            wordBreak: "break-all",
            maxWidth: "100%",
          }}
        >
          {pathname}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", justifyContent: "center" }}>
          <Link to="/" data-action="back-to-overview" style={primaryButton}>
            Back to Overview
          </Link>
          <button
            type="button"
            data-action="go-back"
            onClick={() => window.history.back()}
            style={secondaryButton}
          >
            Go back
          </button>
        </div>
      </div>
    </main>
  );
}

const primaryButton: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "none",
  borderRadius: "var(--radius-md, 8px)",
  background: "var(--color-primary, var(--ag-brand-primary, #6366F1))",
  color: "var(--color-on-primary, #fff)",
  cursor: "pointer",
  fontFamily: "inherit",
  textDecoration: "none",
  display: "inline-block",
};

const secondaryButton: React.CSSProperties = {
  padding: "10px 16px",
  fontSize: 14,
  fontWeight: 500,
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-md, 8px)",
  background: "transparent",
  color: "var(--color-text)",
  cursor: "pointer",
  fontFamily: "inherit",
};
